import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'sonner'
import { LogOut, UserPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { commissions } from '@/lib/mocks'

export const Route = createFileRoute('/portal/vendor')({
  component: VendorPortal,
})

const VENDOR_NAME = 'TalentBridge Associates'

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'info'> = {
  paid: 'success',
  approved: 'info',
  pending: 'warning',
}

const STATUS_LABEL: Record<string, string> = {
  paid: 'Paid',
  approved: 'Approved',
  pending: 'Pending',
}

function VendorPortal() {
  const myCommissions = commissions.filter((c) => c.vendor === VENDOR_NAME)

  const totalPayable = myCommissions
    .filter((c) => c.status !== 'paid')
    .reduce((sum, c) => {
      const num = parseFloat(c.payable.replace(/[₹,]/g, ''))
      return sum + (isNaN(num) ? 0 : num)
    }, 0)

  const formattedPayable = `₹${totalPayable.toLocaleString('en-IN')}`
  const placementCount = myCommissions.length

  function handleSubmit() {
    toast.success('Candidate submission form sent to your email.')
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="border-b border-border bg-background sticky top-0 z-10">
        <div className="mx-auto max-w-3xl px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-semibold text-foreground tracking-tight">Inumellas Consultancy Services</span>
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
              Vendor portal
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:block">{VENDOR_NAME}</span>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground gap-1.5"
              onClick={() => toast('Signed out.')}
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="mx-auto max-w-3xl px-4 py-8 space-y-8">
        {/* Heading + action */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-foreground">
              Your submissions & commissions
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Track candidates you've referred and see the payable commission for each placement.
            </p>
          </div>
          <Button onClick={handleSubmit} className="shrink-0 gap-2">
            <UserPlus className="h-4 w-4" />
            Submit a candidate
          </Button>
        </div>

        {/* Stat boxes */}
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Payable to you</p>
            <p className="text-2xl font-semibold text-foreground mt-1">{formattedPayable}</p>
            <p className="text-xs text-muted-foreground mt-1">Approved + pending</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Placements</p>
            <p className="text-2xl font-semibold text-foreground mt-1">{placementCount}</p>
            <p className="text-xs text-muted-foreground mt-1">All time</p>
          </div>
        </div>

        {/* Commissions table */}
        <div>
          <h2 className="text-sm font-medium text-muted-foreground mb-3">Commission details</h2>
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            {myCommissions.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">
                No commissions recorded yet.
              </div>
            ) : (
              <>
                {/* Desktop table */}
                <table className="w-full hidden sm:table">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">
                        Candidate
                      </th>
                      <th className="text-right text-xs font-medium text-muted-foreground px-4 py-3">
                        Split %
                      </th>
                      <th className="text-right text-xs font-medium text-muted-foreground px-4 py-3">
                        Payable
                      </th>
                      <th className="text-left text-xs font-medium text-muted-foreground px-4 py-3">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {myCommissions.map((c) => (
                      <tr key={c.id} className="hover:bg-muted/40 transition-colors">
                        <td className="px-4 py-3 text-sm font-medium text-foreground">
                          {c.candidate}
                        </td>
                        <td className="px-4 py-3 text-sm text-foreground text-right">
                          {c.splitPct}%
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-foreground text-right">
                          {c.payable}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={STATUS_VARIANT[c.status]}>
                            {STATUS_LABEL[c.status]}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Mobile card stack */}
                <div className="sm:hidden divide-y divide-border">
                  {myCommissions.map((c) => (
                    <div key={c.id} className="px-4 py-4 space-y-2 hover:bg-muted/40 transition-colors">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-foreground">{c.candidate}</span>
                        <Badge variant={STATUS_VARIANT[c.status]}>
                          {STATUS_LABEL[c.status]}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          Split {c.splitPct}%
                        </span>
                        <span className="font-medium text-foreground">{c.payable}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
